import { useCallback, useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import { fetchOnCall, addOnCall, deleteOnCall, INC_STR as S, type OnCallShift } from './incidentsApi'

// datetime-local ("YYYY-MM-DDTHH:mm", local tz) → RFC3339 (UTC).
function toRFC3339(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

export function OnCallScreen() {
  const { toast } = useToast()
  const [rows, setRows] = useState<OnCallShift[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [role, setRole] = useState('primary')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchOnCall(ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  const add = async () => {
    const s = toRFC3339(from)
    const e = toRFC3339(to)
    if (!s || !e) {
      toast(S.failed, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await addOnCall(role, s, e)
      setFrom('')
      setTo('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const del = async (id: number) => {
    try {
      await deleteOnCall(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.oncall} screenId="A17.27" description={S.oncallDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl">
          {/* Add shift */}
          <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <label className="text-xs text-tertiary">
              {S.role}
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="mt-1 block rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
              >
                <option value="primary">{S.primary}</option>
                <option value="secondary">{S.secondary}</option>
              </select>
            </label>
            <label className="text-xs text-tertiary">
              {S.from}
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="mt-1 block rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
              />
            </label>
            <label className="text-xs text-tertiary">
              {S.to}
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="mt-1 block rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
              />
            </label>
            <button
              type="button"
              onClick={add}
              disabled={busy || !from || !to}
              className="rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {S.add}
            </button>
          </div>

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.emptyOncall} />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-default">
              {rows.map((r, i) => (
                <div
                  key={r.id}
                  className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm text-primary">
                      {r.admin || '—'}
                      <span className="ml-2 rounded bg-surface-3 px-1.5 py-0.5 text-xs text-secondary">
                        {r.role === 'primary' ? S.primary : S.secondary}
                      </span>
                    </p>
                    <p className="text-xs text-tertiary">
                      {new Date(r.starts_at).toLocaleString()} → {new Date(r.ends_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => del(r.id)}
                    className="shrink-0 rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger"
                    title={S.delete}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
